import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, CheckCircle2, Circle, Clock, Lock, Send } from "lucide-react";
import AppShell from "@/components/AppShell";
import { FullPageLoader, ProvenanceBadge } from "@/components/Common";
import { api } from "@/lib/api";
import { toast } from "sonner";

const ICON = {
  completed: <CheckCircle2 size={16} className="text-emerald-500" />,
  in_progress: <Clock size={16} className="text-brand" />,
  available: <Circle size={16} className="text-brand-muted" />,
  locked: <Lock size={14} className="text-slate-300" />,
};

export default function StudentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [sending, setSending] = useState(false);

  const load = useCallback(() => {
    api.get(`/founder/students/${id}`).then(({ data }) => setData(data)).catch(() => setData({ error: true }));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const checkIn = async () => {
    setSending(true);
    try {
      await api.post("/founder/intervene", { student_id: id, kind: "check_in" });
      toast.success("Check-in logged.");
      load();
    } catch {
      toast.error("Could not log that.");
    } finally { setSending(false); }
  };

  if (!data) return <FullPageLoader label="Loading student…" />;
  if (data.error) return <AppShell><div className="p-10 text-slate-600">Student not found.</div></AppShell>;

  const { student, business, roadmap, readiness, assets, notes } = data;

  return (
    <AppShell>
      <div className="thin-scroll mx-auto max-w-4xl px-8 py-10 ltifade" data-testid="student-detail">
        <button onClick={() => navigate("/founder")} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800">
          <ArrowLeft size={15} /> Founder dashboard
        </button>

        <div className="mt-4 flex items-start justify-between">
          <div className="flex items-center gap-3">
            <span className="grid h-12 w-12 place-items-center rounded-full bg-brand-surface text-sm font-semibold text-brand-text">{student.initials}</span>
            <div>
              <h1 className="font-heading text-2xl font-semibold tracking-tight">{student.name}</h1>
              <p className="text-sm text-slate-500">{student.email} · {business.name}</p>
            </div>
          </div>
          <button data-testid="checkin-btn" onClick={checkIn} disabled={sending} className="inline-flex items-center gap-2 rounded-lg bg-brand px-4 py-2 text-sm font-medium text-white transition-all duration-200 hover:bg-brand-hover disabled:opacity-50">
            <Send size={15} /> Send check in
          </button>
        </div>

        <div className="mt-6 grid grid-cols-2 gap-3">
          {[["Business readiness", readiness?.business_readiness], ["Launch readiness", readiness?.launch_readiness]].map(([l, v]) => (
            <div key={l} className="rounded-xl border border-brand-line bg-white p-4 shadow-soft">
              <p className="text-sm text-slate-500">{l}</p>
              <p className="mt-0.5 font-heading text-2xl font-semibold">{v ?? 0}%</p>
              <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-brand-surface">
                <div className="h-full rounded-full bg-brand" style={{ width: `${v ?? 0}%` }} />
              </div>
            </div>
          ))}
        </div>

        <div className="mt-6 grid gap-6 lg:grid-cols-2">
          <div>
            <h3 className="font-heading text-lg font-medium">Roadmap</h3>
            <div className="mt-3 overflow-hidden rounded-xl border border-brand-line bg-white shadow-soft">
              {roadmap.map((t) => (
                <div key={t.slug} className="flex items-center gap-2.5 border-b border-brand-line px-4 py-2.5 last:border-0">
                  {ICON[t.status]}
                  <span className="flex-1 text-sm">{t.title}</span>
                  <span className="text-xs capitalize text-slate-400">{t.status.replace("_", " ")}</span>
                </div>
              ))}
            </div>
          </div>
          <div>
            <h3 className="font-heading text-lg font-medium">Support history</h3>
            <div className="mt-3 rounded-xl border border-brand-line bg-white p-4 shadow-soft">
              {notes.length === 0 ? (
                <p className="text-sm text-slate-400">No interventions yet.</p>
              ) : (
                <ul className="space-y-2.5">
                  {notes.map((n) => (
                    <li key={n.note_id} className="text-sm text-slate-600">
                      <span className="rounded-full bg-brand-surface px-2 py-0.5 text-xs font-medium text-brand-text">{n.kind === "review" ? "Review" : "Check-in"}</span>{" "}
                      {n.note}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>

        <h3 className="mt-8 font-heading text-lg font-medium">Assets built ({assets.length})</h3>
        <div className="mt-3 space-y-4">
          {assets.map((a) => (
            <div key={a.asset_id} className="rounded-xl border border-brand-line bg-white p-5 shadow-soft">
              <p className="font-medium">{a.name}</p>
              <div className="mt-3 space-y-3">
                {a.blocks.map((b) => (
                  <div key={b.key}>
                    <div className="flex items-center gap-2">
                      <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">{b.label}</p>
                      <ProvenanceBadge provenance={b.provenance} />
                    </div>
                    <p className="mt-0.5 whitespace-pre-wrap text-sm text-slate-700">{b.content}</p>
                  </div>
                ))}
              </div>
            </div>
          ))}
          {assets.length === 0 && <p className="text-sm text-slate-400">No assets yet.</p>}
        </div>
      </div>
    </AppShell>
  );
}
